import React, { useEffect, useState } from 'react';
import { X, FileDown, Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

/**
 * PartnerDaybookModal - view a single partner's chronological cash movements,
 * filter by date range, export to PDF.
 */
export const PartnerDaybookModal = ({ partnerId, partnerName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await axiosInstance.get(`/cash-pool/partner-daybook/${partnerId}`, { params });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load day book');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [partnerId]);

  const downloadPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const res = await axiosInstance.get(`/cash-pool/partner-daybook/${partnerId}/pdf?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `daybook-${partnerName.toLowerCase().replace(/\s+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="text-lg font-bold">Day Book — {partnerName}</h2>
          <button onClick={onClose} data-testid="daybook-close" className="text-gray-500 hover:text-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-3 p-4 border-b bg-gray-50">
          <Calendar className="w-4 h-4 text-gray-500" />
          <label className="text-xs text-gray-600">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="form-input text-sm w-40" data-testid="daybook-from" />
          <label className="text-xs text-gray-600">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="form-input text-sm w-40" data-testid="daybook-to" />
          <button onClick={fetchData} className="btn-secondary text-sm" data-testid="daybook-apply">Apply</button>
          <div className="flex-1" />
          <button onClick={downloadPdf} className="btn-primary text-sm flex items-center gap-1" data-testid="daybook-pdf">
            <FileDown className="w-4 h-4" /> Download PDF
          </button>
        </div>

        <div className="p-5 overflow-auto flex-1">
          {loading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}
          {!loading && data && (
            <>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="border rounded p-3 bg-indigo-50">
                  <p className="text-xs text-indigo-700">Opening</p>
                  <p className="text-lg font-bold">₹{(data.opening_balance || 0).toLocaleString()}</p>
                </div>
                <div className="border rounded p-3 bg-green-50">
                  <p className="text-xs text-green-700">Inflow</p>
                  <p className="text-lg font-bold text-green-900">+₹{(data.total_inflow || 0).toLocaleString()}</p>
                </div>
                <div className="border rounded p-3 bg-red-50">
                  <p className="text-xs text-red-700">Outflow</p>
                  <p className="text-lg font-bold text-red-900">-₹{(data.total_outflow || 0).toLocaleString()}</p>
                </div>
                <div className="border rounded p-3 bg-gray-50">
                  <p className="text-xs text-gray-700">Closing</p>
                  <p className="text-lg font-bold">₹{(data.closing_balance || 0).toLocaleString()}</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Description</th>
                      <th className="text-right px-3 py-2">In (₹)</th>
                      <th className="text-right px-3 py-2">Out (₹)</th>
                      <th className="text-right px-3 py-2">Balance (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t bg-indigo-50 font-semibold">
                      <td className="px-3 py-2" colSpan={5}>Opening Balance</td>
                      <td className="px-3 py-2 text-right">₹{(data.opening_balance || 0).toLocaleString()}</td>
                    </tr>
                    {(data.entries || []).map((e, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2 whitespace-nowrap">{e.date}</td>
                        <td className="px-3 py-2">{e.type}</td>
                        <td className="px-3 py-2">{e.description}</td>
                        <td className="px-3 py-2 text-right text-green-700">{e.inr_in ? `+${e.inr_in.toLocaleString()}` : '—'}</td>
                        <td className="px-3 py-2 text-right text-red-700">{e.inr_out ? `-${e.inr_out.toLocaleString()}` : '—'}</td>
                        <td className="px-3 py-2 text-right font-medium">{(e.running_balance || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                    {(!data.entries || data.entries.length === 0) && (
                      <tr><td colSpan={6} className="text-center px-3 py-6 text-gray-500">No transactions in this period</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


/**
 * PartnerSummaryModal - side-by-side summary of all partners for review.
 */
export const PartnerSummaryModal = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await axiosInstance.get('/cash-pool/partner-daybook/summary', { params });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const downloadPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const res = await axiosInstance.get(`/cash-pool/partner-daybook/summary/pdf?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'partner-summary.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const cell = (v) => v ? `₹${v.toLocaleString()}` : '—';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[92vh] flex flex-col">
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="text-lg font-bold">All Partners Summary</h2>
          <button onClick={onClose} data-testid="summary-close" className="text-gray-500 hover:text-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex items-center gap-3 p-4 border-b bg-gray-50">
          <Calendar className="w-4 h-4 text-gray-500" />
          <label className="text-xs text-gray-600">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="form-input text-sm w-40" data-testid="summary-from" />
          <label className="text-xs text-gray-600">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="form-input text-sm w-40" data-testid="summary-to" />
          <button onClick={fetchData} className="btn-secondary text-sm" data-testid="summary-apply">Apply</button>
          <div className="flex-1" />
          <button onClick={downloadPdf} className="btn-primary text-sm flex items-center gap-1" data-testid="summary-pdf">
            <FileDown className="w-4 h-4" /> Download PDF
          </button>
        </div>
        <div className="p-5 overflow-auto flex-1">
          {loading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}
          {!loading && data && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-3 py-2">Partner</th>
                    <th className="text-right px-3 py-2">Opening</th>
                    <th className="text-right px-3 py-2">Deposits</th>
                    <th className="text-right px-3 py-2">Transfers In</th>
                    <th className="text-right px-3 py-2">Transfers Out</th>
                    <th className="text-right px-3 py-2">Payouts</th>
                    <th className="text-right px-3 py-2">Expenses</th>
                    <th className="text-right px-3 py-2">Withdrawals</th>
                    <th className="text-right px-3 py-2 font-bold">Closing</th>
                    <th className="text-right px-3 py-2">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).map((r) => (
                    <tr key={r.partner_id} className="border-t">
                      <td className="px-3 py-2 font-medium">{r.partner_name}</td>
                      <td className="px-3 py-2 text-right">{cell(r.opening_balance)}</td>
                      <td className="px-3 py-2 text-right text-green-700">{cell(r.deposits_in)}</td>
                      <td className="px-3 py-2 text-right">{cell(r.transfers_in)}</td>
                      <td className="px-3 py-2 text-right">{cell(r.transfers_out)}</td>
                      <td className="px-3 py-2 text-right text-red-700">{cell(r.payouts_paid)}</td>
                      <td className="px-3 py-2 text-right">{cell(r.expenses)}</td>
                      <td className="px-3 py-2 text-right">{cell(r.withdrawals_out)}</td>
                      <td className="px-3 py-2 text-right font-bold">{cell(r.closing_balance)}</td>
                      <td className={`px-3 py-2 text-right ${r.net_change > 0 ? 'text-green-700' : r.net_change < 0 ? 'text-red-700' : ''}`}>
                        {r.net_change > 0 ? '+' : ''}{r.net_change ? `₹${r.net_change.toLocaleString()}` : '—'}
                      </td>
                    </tr>
                  ))}
                  {data.totals && (
                    <tr className="border-t border-t-2 bg-gray-100 font-bold">
                      <td className="px-3 py-2">TOTAL</td>
                      <td className="px-3 py-2 text-right">{cell(data.totals.opening_balance)}</td>
                      <td className="px-3 py-2 text-right">{cell(data.totals.deposits_in)}</td>
                      <td className="px-3 py-2 text-right">{cell(data.totals.transfers_in)}</td>
                      <td className="px-3 py-2 text-right">{cell(data.totals.transfers_out)}</td>
                      <td className="px-3 py-2 text-right">{cell(data.totals.payouts_paid)}</td>
                      <td className="px-3 py-2 text-right">{cell(data.totals.expenses)}</td>
                      <td className="px-3 py-2 text-right">{cell(data.totals.withdrawals_out)}</td>
                      <td className="px-3 py-2 text-right">{cell(data.totals.closing_balance)}</td>
                      <td className="px-3 py-2 text-right">{cell(data.totals.net_change)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
